/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import { type MouseEvent } from 'react';
import cx from 'classnames';
import root from 'window-or-global';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { replaceHistoryStateOptionsOnClick } from '@/features/common/utils/replaceHistoryStateOptionsOnClick';
import { historyStateOptionAttributes } from '@/constants/historyStateOption';
import { appendSearchTextToUrl } from '@/utils/urlHelpers';
import { SmartLink as Link } from '@/utils/smartLink';
import azCommonStyles from '@/theme/globals.module.scss';
import styles from './styles.module.scss';
import { kebabCase } from '@/utils/kebabCase';
import { sessionStorage } from '@/utils/sessionStorage';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { buildUrlWithAcesParams } from '@/utils/buildUrlWithAcesParams';
type Props = {
  productName?: string;
  CustomElement: 'h2' | 'h3' | 'div';
  url: string | undefined;
  titleStyles?: string;
  isSearchPageType: boolean;
  prodIndex?: number;
  partGroupId?: string;
  brandId?: string;
  positionId?: string;
  uniqueId?: number;
  onProductClick?: () => void;
  isSponsoredCard?: boolean;
};

export const ProductTitle = ({
  productName,
  url,
  titleStyles,
  CustomElement,
  onProductClick,
  isSearchPageType,
  prodIndex,
  partGroupId,
  brandId,
  positionId,
  uniqueId,
  isSponsoredCard,
}: Props) => {
  const { acesEnabled } = useAcesFlags();
  const router = useLegacyRouter();
  // Normalize string | string[] to string before passing to utility
  const searchText = Array.isArray(router.query.searchText)
    ? router.query.searchText[0]
    : router.query.searchText;
  const filterByKeyWord = Array.isArray(router.query.filterByKeyWord)
    ? router.query.filterByKeyWord[0]
    : router.query.filterByKeyWord;

  const updatedUrl = appendSearchTextToUrl(url, searchText, filterByKeyWord);

  const urlWithAcesParams = acesEnabled
    ? buildUrlWithAcesParams(updatedUrl, { partGroupId, brandId, positionId, uniqueId })
    : updatedUrl;

  const productNameKebabCase = kebabCase(productName ?? '');

  const handleClick = () => {
    onProductClick?.();
    if (isSearchPageType) {
      sessionStorage.setItem(
        'productRank',
        isSponsoredCard ? 'Recommended' : String((prodIndex ?? 0) + 1),
      );
    }
    replaceHistoryStateOptionsOnClick(
      historyStateOptionAttributes.latestSelectedPageYOffset,
      root.scrollY || root.pageYOffset,
    );
  };

  const handleAuxClick = (e: MouseEvent) => {
    // Middle mouse button click
    if (e.button === 1) {
      onProductClick?.();
    }
  };

  const El = CustomElement as React.ElementType;

  return (
    <Link
      to={urlWithAcesParams}
      maskedURL={updatedUrl}
      samePage
      className={azCommonStyles['az-color-inherit']}
      data-testid={`${productNameKebabCase}-product-title-link`}
    >
      <El
        className={
          titleStyles
            ? titleStyles
            : cx(
                azCommonStyles['az-body-1-heavy'],
                styles.titleText,
                styles.newFulfillmentTitle,
                'product-title',
              )
        }
        onClick={handleClick}
        onAuxClick={handleAuxClick}
        data-testid={`${productNameKebabCase}-product-title`}
      >
        {productName}
      </El>
    </Link>
  );
};
